import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateDMY, formatMinutesToHM } from '../ui/format';
import SegmentedControl from '../components/SegmentedControl.jsx';
import { API_BASE_URL, fetchProjectsList, fetchTeamLoad, fetchPersonWeeklyWorkload, fetchPlanningLookups, removePlannedTaskDay } from '../api';
import { getActiveTheme, isRedFocusThemeName, resolveThemeName, shadows, radii } from '../ui/tokens';
import Calendar2 from '../components/manager/Calendar2.jsx';
import SharedAddWorkModal from '../components/manager/SharedAddWorkModal.jsx';
import { useAppRefresh } from '../context/AppRefreshContext';

const SHOW_RISK_UI = false;

function RangeSwitcher({ range, onChange }) {
  return (
    <SegmentedControl ariaLabel="Range" options={[
      { key: 'last_week', label: 'Last week' },
      { key: 'this_week', label: 'This week' },
      { key: 'next_week', label: 'Next week' },
      { key: 'last_month', label: 'Last month' },
      { key: 'this_month', label: 'This month' },
      { key: 'total', label: 'Total' },
    ]} value={range} onChange={onChange} />
  );
}

function coerceYmd(v) {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

function todayYmdLocal() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isDoneProject(proj, todayYmd) {
  const endYmd = coerceYmd(proj?.end_date);
  return !!endYmd && endYmd < todayYmd;
}

function statusFromUsers(users) {
  const hasOver = users.some(u => u.status === 'overbooked');
  const hasWarn = !hasOver && users.some(u => u.status === 'warning');
  const hasOk = !hasOver && !hasWarn && users.some(u => u.status === 'ok');
  return hasOver ? 'overbooked' : hasWarn ? 'warning' : hasOk ? 'ok' : 'neutral';
}

function PortfolioCard({ proj, stats, onOpenWork, onOpenProject, onExport }) {
  const [hover, setHover] = useState(false);
  const [primaryBtnHover, setPrimaryBtnHover] = useState(false);
  const [secondaryBtnHover, setSecondaryBtnHover] = useState(false);
  const isRedFocus = isRedFocusThemeName(resolveThemeName());
  const themeName = resolveThemeName();
  const isAuroraLight = themeName === 'auroraExecutiveLight';
  const isRedPremium = themeName === 'redLightPremium';
  const isPremiumLight = themeName === 'auroraExecutiveLight' || themeName === 'redLightPremium';
  const activeTheme = getActiveTheme();
  const warmTheme = isRedFocus ? activeTheme : null;
  const planned = stats.plannedTotal || 0;
  const logged = stats.loggedTotal || 0;
  const pct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
  const statusLabel = stats.status === 'overbooked' ? 'At risk' : (stats.status === 'warning' ? 'Warning' : (stats.status === 'ok' ? 'On track' : 'No data'));
  const statusBg = stats.status === 'overbooked' ? '#8C5A5A' : (stats.status === 'warning' ? '#9A8F6A' : (stats.status === 'ok' ? '#5F7F7A' : '#6B7280'));
  return (
    <div
      className={
        isPremiumLight
          ? 'p-3 cursor-pointer'
          : 'rounded-lg p-3 bg-gradient-to-r from-indigo-50 via-pink-50 to-amber-50 dark:from-slate-800 dark:via-fuchsia-900/30 dark:to-amber-900/30 border border-gray-200 dark:border-gray-800 cursor-pointer'
      }
      style={{
        borderRadius: isPremiumLight ? 12 : undefined,
        border: isPremiumLight ? `1px solid ${activeTheme?.border || '#E5E9F0'}` : undefined,
        background: isPremiumLight
          ? (isRedPremium
            ? 'linear-gradient(135deg, rgba(215,38,56,0.14) 0%, rgba(215,38,56,0.06) 34%, rgba(255,255,255,0.88) 100%)'
            : (activeTheme?.surface || '#FFFFFF'))
          : undefined,
        boxShadow: isPremiumLight
          ? (hover ? (activeTheme?.cardShadowHover || '0 8px 24px rgba(0,0,0,0.06)') : (activeTheme?.cardShadow || '0 4px 20px rgba(0,0,0,0.04)'))
          : shadows.sm,
        transform: isPremiumLight && hover ? 'translateY(-2px)' : undefined,
        transition: isPremiumLight ? 'all 120ms ease' : undefined,
        ...(isRedFocus
          ? { background: `linear-gradient(90deg, ${warmTheme?.surface2 || '#fff1f2'} 0%, #ffffff 100%)` }
          : null),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onClick={() => onOpenProject(proj)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenProject(proj);
        }
      }}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-base font-semibold truncate">{proj.name}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{proj.client_name || '—'}</div>
          {proj.manager_first_name ? (
            <div className="text-xs text-gray-600 dark:text-gray-400 truncate">Manager: {proj.manager_first_name}</div>
          ) : null}
        </div>
        {SHOW_RISK_UI && (
          <span className="text-[11px] px-2 py-0.5 rounded text-white" style={{ background: statusBg }}>{statusLabel}</span>
        )}
      </div>
      <div className="mt-1 text-[12px] text-gray-700 dark:text-gray-300">{formatDateDMY(proj.start_date)} – {formatDateDMY(proj.end_date)}</div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
        <div>Planned {formatMinutesToHM(planned)}</div>
        <div>Logged {formatMinutesToHM(logged)}</div>
        <div className="hidden sm:block">P {formatMinutesToHM(stats.assignedTotal || 0)}</div>
        <div className="hidden sm:block">O {formatMinutesToHM(stats.otherTotal || 0)}</div>
      </div>
      <div className="mt-2">
        <div
          className={isPremiumLight ? 'h-2 rounded overflow-hidden' : 'h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden'}
          style={{ background: isPremiumLight ? (activeTheme?.progressBg || activeTheme?.border || '#E5E9F0') : undefined }}
        >
          <div
            className={isPremiumLight ? 'h-2' : 'h-2 bg-gray-500'}
            style={{
              width: `${pct}%`,
              background: isPremiumLight ? (activeTheme?.primary || '#5063A8') : undefined,
              transition: isPremiumLight ? 'width 200ms ease' : undefined,
            }}
          />
        </div>
        <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">{planned > 0 ? `${pct}% of planned` : 'No planned'}</div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          className={`px-3 py-1 rounded text-white text-xs active:scale-[0.98] ${isRedFocus ? 'hover:brightness-95' : ''}`}
          style={
            isAuroraLight
              ? {
                  background: primaryBtnHover ? (activeTheme?.primaryHover || '#4457A0') : (activeTheme?.primary || '#5063A8'),
                  transition: 'all 120ms ease',
                }
              : (isRedFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined)
          }
          onClick={(e) => { e.stopPropagation(); onOpenWork(proj); }}
          onMouseEnter={() => setPrimaryBtnHover(true)}
          onMouseLeave={() => setPrimaryBtnHover(false)}
        >
          Assign Work
        </button>
        <button
          type="button"
          className="px-3 py-1 rounded text-xs active:scale-[0.98]"
          style={
            isAuroraLight
              ? {
                  background: secondaryBtnHover ? 'rgba(80,99,168,0.05)' : (activeTheme?.surface2 || '#EEF1F6'),
                  color: activeTheme?.text || '#1F2937',
                  transition: 'all 120ms ease',
                }
              : undefined
          }
          onClick={(e) => { e.stopPropagation(); onExport(proj); }}
          onMouseEnter={() => setSecondaryBtnHover(true)}
          onMouseLeave={() => setSecondaryBtnHover(false)}
        >
          Export
        </button>
      </div>
    </div>
  );
}

function ProjectsTable({ rows, onOpenProject, onOpenWork, onExport }) {
  const isRedFocus = isRedFocusThemeName(resolveThemeName());
  const warmTheme = isRedFocus ? getActiveTheme() : null;
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const sorted = useMemo(() => {
    const arr = rows.slice();
    const cmp = (a,b) => {
      const ak = a[sort.key]; const bk = b[sort.key];
      if (typeof ak === 'number' && typeof bk === 'number') return sort.dir==='asc' ? ak-bk : bk-ak;
      return sort.dir==='asc' ? String(ak||'').localeCompare(String(bk||'')) : String(bk||'').localeCompare(String(ak||''));
    };
    return arr.sort(cmp);
  }, [rows, sort]);
  const th = (key, label) => (
    <th className="px-3 py-2 text-left text-[12px] cursor-pointer" onClick={()=> setSort(s=>({ key, dir: (s.key===key && s.dir==='asc') ? 'desc' : 'asc' }))}>{label}</th>
  );
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[720px] w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {th('name','Project')}
            {th('client_name','Client')}
            {th('dates','Dates')}
            {th('planned','Planned')}
            {th('logged','Logged')}
            {th('assigned','P')}
            {th('other','O')}
            {th('utilPct','Util %')}
            {th('statusLabel','Status')}
            <th className="px-3 py-2 text-left text-[12px]">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800">
              <td className="px-3 py-2">{r.name}</td>
              <td className="px-3 py-2">
                <div className="truncate">{r.client_name}</div>
                {r.manager_first_name ? (
                  <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">Manager: {r.manager_first_name}</div>
                ) : null}
              </td>
              <td className="px-3 py-2">{r.dates}</td>
              <td className="px-3 py-2">{formatMinutesToHM(r.planned)}</td>
              <td className="px-3 py-2">{formatMinutesToHM(r.logged)}</td>
              <td className="px-3 py-2">{formatMinutesToHM(r.assigned)}</td>
              <td className="px-3 py-2">{formatMinutesToHM(r.other)}</td>
              <td className="px-3 py-2">{r.utilPct}%</td>
              <td className="px-3 py-2">{r.statusLabel}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 rounded bg-gray-900 text-white text-[12px]" onClick={()=> onOpenProject({ id: r.id })}>Open</button>
                  <button
                    className={`px-2 py-1 rounded text-white text-[12px] ${isRedFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
                    style={isRedFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
                    onClick={()=> onOpenWork({ id: r.id, name: r.name, client_name: r.client_name })}
                  >Assign Work</button>
                  <button className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-[12px]" onClick={()=> onExport({ id: r.id })}>Export</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DoneProjectsSection({ count, collapsed, onToggle, children }) {
  const themeName = resolveThemeName();
  const theme = getActiveTheme();
  const isPremiumLight = themeName === 'auroraExecutiveLight' || themeName === 'redLightPremium';
  return (
    <div className="mt-6">
      <div
        className={isPremiumLight ? 'rounded-[14px] p-3' : 'rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/40 p-3'}
        style={isPremiumLight ? {
          border: `1px solid ${theme?.border || '#E5E9F0'}`,
          background: theme?.surface2 || '#F7F8FB',
          boxShadow: theme?.cardShadow || shadows.sm,
        } : undefined}
      >
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 text-left"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <div>
            <div className="text-sm font-semibold">Done projects</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">{count} {count === 1 ? 'project' : 'projects'}</div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <span>{collapsed ? 'Show' : 'Hide'}</span>
            <span aria-hidden>{collapsed ? '▸' : '▾'}</span>
          </div>
        </button>
        {!collapsed && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  ok: '#5F7F7A',
  warning: '#9A8F6A',
  overbooked: '#8C5A5A',
  neutral: '#6B7280',
};

function TeamTile({ user, onClick }) {
  const bg = STATUS_COLORS[user.status] || STATUS_COLORS.neutral;
  const planned = user.planned_minutes_total || 0;
  const logged = user.logged_minutes_total || 0;
  const pct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
  const colorClass = planned > 0
    ? (pct < 80 ? 'bg-green-500' : (pct <= 100 ? 'bg-yellow-500' : 'bg-red-500'))
    : 'bg-gray-300';
  return (
    <button
      onClick={onClick}
      className="group relative flex items-center justify-center text-center p-3 hover:brightness-110 transition"
      style={{
        background: bg,
        clipPath: 'polygon(30% 5%, 70% 5%, 85% 15%, 95% 30%, 95% 70%, 85% 85%, 70% 95%, 30% 95%, 15% 85%, 5% 70%, 5% 30%, 15% 15%)',
        minHeight: 140,
      }}
      aria-label={`Open workload for ${user.display_name || 'User'}`}
    >
      <div className="text-white">
        <div className="font-semibold text-base mb-1">{user.display_name || 'Unnamed'}</div>
        <div className="text-xs opacity-90">Planned: {formatMinutesToHM(user.planned_minutes_total || 0)}</div>
        <div className="text-xs opacity-90">Logged: {formatMinutesToHM(user.logged_minutes_total || 0)}</div>
        <div className="text-[11px] opacity-80 mt-1">Assigned: {formatMinutesToHM(user.logged_assigned_minutes_total || 0)} • Other: {formatMinutesToHM(user.logged_other_minutes_total || 0)}</div>
        <div className="mt-2">
          <div className="h-1.5 bg-white/30 rounded">
            <div className={`h-1.5 ${colorClass} rounded`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] mt-0.5 opacity-80">{planned > 0 ? `${pct}% of planned` : 'No planned'}</div>
        </div>
      </div>
    </button>
  );
}

function WorkloadDrawer({ open, onClose, user, range, from, to, projectId, projectLabel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState([]);
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [totals, setTotals] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editAssignmentId, setEditAssignmentId] = useState(null);
  const [rowActionLoading, setRowActionLoading] = useState('');

  // If the drawer is closed, also close any nested edit modal.
  useEffect(() => {
    if (open) return;
    setEditAssignmentId(null);
    setRowActionLoading('');
  }, [open]);

  const parseYmdUtc = (ymd) => {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd))) return null;
    const d = new Date(`${ymd}T00:00:00Z`);
    return isNaN(d.getTime()) ? null : d;
  };
  const toYmdUtc = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const mondayOfYmd = (ymd) => {
    const d = parseYmdUtc(ymd);
    if (!d) return '';
    const wd = d.getUTCDay(); // 0=Sun, 1=Mon
    const deltaToMon = (wd + 6) % 7;
    d.setUTCDate(d.getUTCDate() - deltaToMon);
    return toYmdUtc(d);
  };
  const addDaysYmd = (ymd, deltaDays) => {
    const d = parseYmdUtc(ymd);
    if (!d) return '';
    d.setUTCDate(d.getUTCDate() + Number(deltaDays || 0));
    return toYmdUtc(d);
  };
  const formatYmdDM = (ymd) => {
    const d = parseYmdUtc(ymd);
    if (!d) return ymd;
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}`;
  };

  useEffect(() => {
    if (!open) return;
    const base = (from && to) ? mondayOfYmd(from) : mondayOfYmd(toYmdUtc(new Date()));
    setWeekStart(base);
  }, [open, from, to]);

  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      // If the edit modal is open, let it handle Escape.
      if (editAssignmentId) return;
      onClose();
    }
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, editAssignmentId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !user || !weekStart) return;
      setLoading(true); setError('');
      try {
        const res = await fetchPersonWeeklyWorkload(user.user_id, weekStart, projectId || null);
        if (!cancelled && res) {
          setWeekEnd(res.weekEnd || addDaysYmd(weekStart, 6));
          setTotals(res.totals || null);
          if (Array.isArray(res.days)) setDays(res.days);
          else setDays([]);
        }
      } catch (err) {
        if (!cancelled) setError('Workload data unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open, user, weekStart, projectId, reloadKey]);

  const handleRemovePlannedDay = async (assignmentId, ymd) => {
    const aid = assignmentId != null ? String(assignmentId) : '';
    if (!aid || !ymd) return;
    const userId = user?.user_id;
    if (!userId) return;

    const ok = window.confirm('Remove this task from this day?');
    if (!ok) return;

    setRowActionLoading(`${aid}|${ymd}`);
    setError('');
    try {
      await removePlannedTaskDay(aid, ymd, { userId });
      setReloadKey(k => k + 1);
    } catch (err) {
      const msg = String(err?.message || '').trim();
      setError(msg || 'Failed to remove day');
    } finally {
      setRowActionLoading('');
    }
  };

  const effectiveWeekEnd = weekEnd || addDaysYmd(weekStart, 6);

  return (
    <div className={`fixed inset-0 z-40 ${open ? '' : 'pointer-events-none'}`}>
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 backdrop-blur-[6px] transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
      />
      <div className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white dark:bg-gray-900 shadow-xl transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <div className="text-lg font-semibold">{user?.display_name || 'User'}{projectLabel ? ` — ${projectLabel}` : ''}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Week • {weekStart ? formatYmdDM(weekStart) : '—'} – {effectiveWeekEnd ? formatYmdDM(effectiveWeekEnd) : '—'}</div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setWeekStart(ws => addDaysYmd(ws, -7))}
                disabled={!weekStart || loading}
              >
                Prev
              </button>
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setWeekStart(ws => addDaysYmd(ws, 7))}
                disabled={!weekStart || loading}
              >
                Next
              </button>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-56px)]">
          {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && totals && (
            <div className="border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
              <div className="text-sm font-medium mb-1">Totals</div>
              <div className="text-xs grid grid-cols-2 gap-2">
                <div>Planned: {formatMinutesToHM(totals.planned_minutes || 0)}</div>
                <div>Logged: {formatMinutesToHM(totals.logged_minutes || 0)}</div>
              </div>
              <div className="text-[11px] text-gray-600 dark:text-gray-300 mt-2">Assigned: {formatMinutesToHM(totals.logged_assigned_minutes || 0)} • Other: {formatMinutesToHM(totals.logged_other_minutes || 0)}</div>
            </div>
          )}
          {!loading && !error && days.map(d => (
            <div key={d.date} className="border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-sm">{formatYmdDM(d.date)} <span className="text-xs text-gray-500 dark:text-gray-400">({d.date})</span></div>
                {(d.planned_minutes || 0) > 480 && (
                  <span className="text-xs px-2 py-0.5 rounded bg-red-200/40 text-red-800 dark:bg-red-900/40 dark:text-red-200">Over 8h</span>
                )}
              </div>
              <div className="text-xs grid grid-cols-3 gap-2">
                <div>Planned: {formatMinutesToHM(d.planned_minutes || 0)}</div>
                <div>Logged: {formatMinutesToHM(d.logged_minutes || 0)}</div>
                <div>Remaining: {formatMinutesToHM((d.planned_minutes || 0) - (d.logged_minutes || 0))}</div>
              </div>
              <div className="text-[11px] text-gray-600 dark:text-gray-300 mt-2">Assigned: {formatMinutesToHM(d.logged_assigned_minutes || 0)} • Other: {formatMinutesToHM(d.logged_other_minutes || 0)}</div>

              {Array.isArray(d.tasks) && d.tasks.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Tasks</div>
                  {d.tasks.map(t => (
                    <div key={t.assignment_id} className="text-[11px] flex items-center justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white/50 dark:bg-gray-900/40">
                      <div className="truncate">
                        <span className="font-medium">{t.project_name || projectLabel || '—'}</span>
                        <span className="text-gray-600 dark:text-gray-400">{t.job_name ? ` • ${t.job_name}` : ''}{t.task_name ? ` › ${t.task_name}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-gray-600 dark:text-gray-400">P {formatMinutesToHM(t.planned_minutes || 0)}</span>
                        <span className="text-gray-600 dark:text-gray-400">L {formatMinutesToHM(t.logged_minutes || 0)}</span>
                        <button
                          type="button"
                          className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-semibold disabled:opacity-60 disabled:hover:bg-indigo-600"
                          onClick={() => setEditAssignmentId(t.assignment_id)}
                          disabled={!t.assignment_id || loading}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                          onClick={() => handleRemovePlannedDay(t.assignment_id, d.date)}
                          disabled={!t.assignment_id || loading || rowActionLoading === `${String(t.assignment_id)}|${String(d.date)}` || (Number(t.logged_minutes || 0) > 0)}
                          title={(Number(t.logged_minutes || 0) > 0) ? 'Cannot remove: has logged time' : 'Remove this day'}
                        >
                          {rowActionLoading === `${String(t.assignment_id)}|${String(d.date)}` ? '…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!loading && !error && days.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No data</div>
          )}
        </div>
      </div>

      <SharedAddWorkModal
        open={!!editAssignmentId}
        assignmentId={editAssignmentId}
        mode="edit"
        project={null}
        rangeKey="this_week"
        onClose={() => setEditAssignmentId(null)}
        onSaved={() => {
          setEditAssignmentId(null);
          setReloadKey(k => k + 1);
        }}
      />
    </div>
  );
}

export default function Projects() {
  const nav = useNavigate();
  const { refreshKey: appRefreshKey } = useAppRefresh();
  const [tab, setTab] = useState('projects');
  const [view, setView] = useState(() => { try { return localStorage.getItem('projects.view') || 'grid'; } catch { return 'grid'; } });
  const [range, setRange] = useState(() => { try { return localStorage.getItem('projects.range') || 'this_week'; } catch { return 'this_week'; } });
  const [projectId, setProjectId] = useState(() => { try { const v = localStorage.getItem('projects.projectId'); return v ? Number(v) : null; } catch { return null; } });
  const [managerId, setManagerId] = useState(() => { try { return localStorage.getItem('projects.managerId') || ''; } catch { return ''; } });
  const [managerOptions, setManagerOptions] = useState([]); // [{id, display_name}]
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [workProject, setWorkProject] = useState(null);
  const [doneProjectsCollapsed, setDoneProjectsCollapsed] = useState(true);
  // NOTE: People tab intentionally removed from Projects page.

  const handleTabChange = (nextTab) => {
    // Prevent stale overlays/modals from appearing when switching tabs.
    setWorkProject(null);
    setTab(nextTab);
  };

  useEffect(() => { try { localStorage.removeItem('projects.tab'); } catch {} }, []);
  useEffect(() => { try { localStorage.setItem('projects.view', view); } catch {} }, [view]);
  useEffect(() => { try { localStorage.setItem('projects.range', range); } catch {} }, [range]);
  useEffect(() => { try { if (projectId != null) localStorage.setItem('projects.projectId', String(projectId)); else localStorage.removeItem('projects.projectId'); } catch {} }, [projectId]);
  useEffect(() => { try { localStorage.setItem('projects.managerId', String(managerId || '')); } catch {} }, [managerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lk = await fetchPlanningLookups({ redirectOn401: false });
        const order = ['Andrea', 'Vinko', 'Sanela'];
        const allowed = new Set(order);
        const opts = (Array.isArray(lk?.managers) ? lk.managers : [])
          .filter(m => allowed.has(String(m.display_name || '').trim()))
          .sort((a, b) => order.indexOf(String(a.display_name || '').trim()) - order.indexOf(String(b.display_name || '').trim()));
        if (!cancelled) setManagerOptions(opts);
      } catch {
        if (!cancelled) setManagerOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        const list = await fetchProjectsList();
        setProjects(Array.isArray(list) ? list : []);
        const entries = await Promise.all((Array.isArray(list)?list:[]).map(async p => {
          try {
            const projectFrom = coerceYmd(p?.start_date);
            const projectTo = coerceYmd(p?.end_date);
            const isTotal = String(range) === 'total';
            const tl = await fetchTeamLoad(
              isTotal ? 'this_week' : range,
              p.id,
              (isTotal && projectFrom && projectTo) ? { from: projectFrom, to: projectTo } : {}
            );
            const users = Array.isArray(tl?.users) ? tl.users : [];
            const plannedTotal = users.reduce((acc, u) => acc + (u.planned_minutes_total || 0), 0);
            const loggedTotal = users.reduce((acc, u) => acc + (u.logged_minutes_total || 0), 0);
            const assignedTotal = users.reduce((acc, u) => acc + (u.logged_assigned_minutes_total || 0), 0);
            const otherTotal = users.reduce((acc, u) => acc + (u.logged_other_minutes_total || 0), 0);
            const status = statusFromUsers(users);
            return [p.id, { plannedTotal, loggedTotal, assignedTotal, otherTotal, status }];
          } catch (_) {
            return [p.id, { plannedTotal: 0, loggedTotal: 0, assignedTotal: 0, otherTotal: 0, status: 'neutral' }];
          }
        }));
        setStats(new Map(entries));
      } catch (err) {
        if (!cancelled) setError('Failed to load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [range, appRefreshKey]);

  const managerFirstNameByUserId = useMemo(() => {
    const map = new Map();
    (Array.isArray(managerOptions) ? managerOptions : []).forEach(m => {
      const id = m?.id;
      const dn = String(m?.display_name || '').trim();
      if (id == null || !dn) return;
      const first = dn.split(/\s+/).filter(Boolean)[0] || '';
      if (!first) return;
      map.set(String(id), first);
    });
    return map;
  }, [managerOptions]);

  const projectsWithManagerLabel = useMemo(() => {
    return (Array.isArray(projects) ? projects : []).map(p => {
      const first = p?.created_by_user_id != null ? (managerFirstNameByUserId.get(String(p.created_by_user_id)) || '') : '';
      return { ...p, manager_first_name: first || null };
    });
  }, [projects, managerFirstNameByUserId]);

  const filteredProjects = useMemo(() => {
    const list = Array.isArray(projectsWithManagerLabel) ? projectsWithManagerLabel : [];
    if (!managerId) return list;
    return list.filter(p => String(p.created_by_user_id || '') === String(managerId));
  }, [projectsWithManagerLabel, managerId]);

  const { activeProjects, doneProjects } = useMemo(() => {
    const todayYmd = todayYmdLocal();
    return filteredProjects.reduce((acc, proj) => {
      if (isDoneProject(proj, todayYmd)) acc.doneProjects.push(proj);
      else acc.activeProjects.push(proj);
      return acc;
    }, { activeProjects: [], doneProjects: [] });
  }, [filteredProjects]);

  const visibleActiveProjects = useMemo(
    () => activeProjects.filter(p => projectId ? p.id === projectId : true),
    [activeProjects, projectId]
  );

  const visibleDoneProjects = useMemo(
    () => doneProjects.filter(p => projectId ? p.id === projectId : true),
    [doneProjects, projectId]
  );

  useEffect(() => {
    if (projectId == null) return;
    const ok = filteredProjects.some(p => p.id === projectId);
    if (!ok) setProjectId(null);
  }, [projectId, filteredProjects]);

  const rowsForTable = useMemo(() => {
    return filteredProjects.map(p => {
      const st = stats.get(p.id) || {};
      const planned = st.plannedTotal || 0;
      const logged = st.loggedTotal || 0;
      const assigned = st.assignedTotal || 0;
      const other = st.otherTotal || 0;
      const utilPct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
      const statusLabel = st.status === 'overbooked' ? 'At risk' : (st.status === 'warning' ? 'Warning' : (st.status === 'ok' ? 'On track' : 'No data'));
      return {
        id: p.id,
        name: p.name,
        client_name: p.client_name,
        manager_first_name: p.manager_first_name || null,
        dates: `${formatDateDMY(p.start_date)} – ${formatDateDMY(p.end_date)}`,
        planned, logged, assigned, other, utilPct, statusLabel,
      };
    });
  }, [filteredProjects, stats]);

  const activeRowsForTable = useMemo(
    () => rowsForTable.filter(r => visibleActiveProjects.some(p => p.id === r.id)),
    [rowsForTable, visibleActiveProjects]
  );

  const doneRowsForTable = useMemo(
    () => rowsForTable.filter(r => visibleDoneProjects.some(p => p.id === r.id)),
    [rowsForTable, visibleDoneProjects]
  );

  const ProjectFilter = ({ value, onChange }) => (
    <select
      value={value ?? ''}
      onChange={e=>onChange(e.target.value?Number(e.target.value):null)}
      className={
        (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
          ? 'ui-focus px-2 py-1 text-sm rounded-[10px]'
          : 'px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
      }
      style={
        (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
          ? { background: getActiveTheme()?.surface2 || '#EEF1F6', border: '1px solid transparent' }
          : undefined
      }
    >
      <option value="">All projects</option>
      {filteredProjects.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
    </select>
  );

  const ManagerFilter = ({ value, onChange }) => (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={
        (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
          ? 'ui-focus px-2 py-1 text-sm rounded-[10px]'
          : 'px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
      }
      style={
        (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
          ? { background: getActiveTheme()?.surface2 || '#EEF1F6', border: '1px solid transparent' }
          : undefined
      }
      title="Manager"
    >
      <option value="">All</option>
      {managerOptions.map(m => (
        <option key={m.id} value={m.id}>{m.display_name}</option>
      ))}
    </select>
  );

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">Projects</div>
        <div className="flex items-center gap-2">
          <SegmentedControl ariaLabel="Projects tabs" ariaRole="tablist" options={[{key:'projects',label:'Projects'},{key:'calendar',label:'Calendar'}]} value={tab} onChange={handleTabChange} />
          
          {tab !== 'calendar' && (
            <>
              {tab === 'projects' && (
                <SegmentedControl ariaLabel="View" options={[{key:'grid',label:'Grid'},{key:'table',label:'Table'}]} value={view} onChange={setView} />
              )}
              <div className="flex flex-col">
                <div className="text-[11px] text-gray-500">Managers</div>
                <ManagerFilter value={managerId} onChange={setManagerId} />
              </div>
              <RangeSwitcher range={range} onChange={setRange} />
              <ProjectFilter value={projectId} onChange={setProjectId} />
            </>
          )}
        </div>
      </div>

      {tab === 'calendar' && (
        <Calendar2 today={new Date()} storageNamespace="projectsPage" enableAssignWorkCta />
      )}

      {tab === 'projects' && (
        <>
          {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && (
            view === 'grid' ? (
              <>
                {visibleActiveProjects.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visibleActiveProjects.map(p => (
                      <PortfolioCard key={p.id}
                                     proj={p}
                                     stats={stats.get(p.id) || {}}
                                     onOpenProject={(proj)=> nav(`/projects/${proj.id}`)}
                                     onOpenWork={(proj)=> setWorkProject(proj)}
                                     onExport={(proj)=> nav('/reports')}
                      />
                    ))}
                  </div>
                )}
                {visibleDoneProjects.length > 0 && (
                  <DoneProjectsSection
                    count={visibleDoneProjects.length}
                    collapsed={doneProjectsCollapsed}
                    onToggle={() => setDoneProjectsCollapsed(v => !v)}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {visibleDoneProjects.map(p => (
                        <PortfolioCard key={p.id}
                                       proj={p}
                                       stats={stats.get(p.id) || {}}
                                       onOpenProject={(proj)=> nav(`/projects/${proj.id}`)}
                                       onOpenWork={(proj)=> setWorkProject(proj)}
                                       onExport={(proj)=> nav('/reports')}
                        />
                      ))}
                    </div>
                  </DoneProjectsSection>
                )}
                {visibleActiveProjects.length === 0 && visibleDoneProjects.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">No projects</div>
                )}
              </>
            ) : (
              <>
                {activeRowsForTable.length > 0 && (
                  <ProjectsTable
                    rows={activeRowsForTable}
                    onOpenProject={(proj)=> nav(`/projects/${proj.id}`)}
                    onOpenWork={(proj)=> setWorkProject(proj)}
                    onExport={(proj)=> nav('/reports')}
                  />
                )}
                {doneRowsForTable.length > 0 && (
                  <DoneProjectsSection
                    count={doneRowsForTable.length}
                    collapsed={doneProjectsCollapsed}
                    onToggle={() => setDoneProjectsCollapsed(v => !v)}
                  >
                    <ProjectsTable
                      rows={doneRowsForTable}
                      onOpenProject={(proj)=> nav(`/projects/${proj.id}`)}
                      onOpenWork={(proj)=> setWorkProject(proj)}
                      onExport={(proj)=> nav('/reports')}
                    />
                  </DoneProjectsSection>
                )}
                {activeRowsForTable.length === 0 && doneRowsForTable.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">No projects</div>
                )}
              </>
            )
          )}
        </>
      )}

      {tab === 'projects' && workProject && (
        <SharedAddWorkModal
          project={workProject}
          rangeKey={range}
          onClose={() => setWorkProject(null)}
          onCreated={async () => {
          try {
            const list = await fetchProjectsList();
            setProjects(Array.isArray(list) ? list : []);
            const entries = await Promise.all((Array.isArray(list)?list:[]).map(async p => {
              try {
                const projectFrom = coerceYmd(p?.start_date);
                const projectTo = coerceYmd(p?.end_date);
                const isTotal = String(range) === 'total';
                const tl = await fetchTeamLoad(
                  isTotal ? 'this_week' : range,
                  p.id,
                  (isTotal && projectFrom && projectTo) ? { from: projectFrom, to: projectTo } : {}
                );
                const users = Array.isArray(tl?.users) ? tl.users : [];
                const plannedTotal = users.reduce((acc, u) => acc + (u.planned_minutes_total || 0), 0);
                const loggedTotal = users.reduce((acc, u) => acc + (u.logged_minutes_total || 0), 0);
                const assignedTotal = users.reduce((acc, u) => acc + (u.logged_assigned_minutes_total || 0), 0);
                const otherTotal = users.reduce((acc, u) => acc + (u.logged_other_minutes_total || 0), 0);
                const status = statusFromUsers(users);
                return [p.id, { plannedTotal, loggedTotal, assignedTotal, otherTotal, status }];
              } catch (_) {
                return [p.id, { plannedTotal: 0, loggedTotal: 0, assignedTotal: 0, otherTotal: 0, status: 'neutral' }];
              }
            }));
            setStats(new Map(entries));
          } catch {}
        }}
        />
      )}
    </div>
  );
}
